/* eslint react-hooks/exhaustive-deps: "warn" */

import { type MouseEvent, useCallback, useContext, useRef, useState } from 'react';

import cn from 'classnames';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './ExtensionHeroSection.module.scss';

type Translate = (key: string) => string;

type TabId = 'status' | 'financials' | 'structure' | 'shareholders' | 'people';

type Row = {
  k: string;
  v: string;
  variant?: 'ok' | 'txt' | 'addr';
};

type TreeNode = {
  label: string;
  meta: string;
  level: 0 | 1 | 2;
};

type Person = {
  id: string;
  name: string;
  role: string;
  emailMasked: string;
  emailFull: string;
  phoneMasked: string;
  phoneFull: string;
};

const getTabs = (t: Translate): { id: TabId; label: string }[] => [
  { id: 'status', label: t('Status') },
  { id: 'financials', label: t('Financials') },
  { id: 'structure', label: t('Structure') },
  { id: 'shareholders', label: t('Shareholders') },
  { id: 'people', label: t('Key people') },
];

const getStatusRows = (t: Translate): Row[] => [
  { k: t('Registration number'), v: '07734112' },
  { k: t('Registry status'), v: t('Active'), variant: 'ok' },
  { k: t('VAT number'), v: 'GB 417 2298 44' },
  { k: t('Legal form'), v: t('Private limited'), variant: 'txt' },
  { k: 'SIC', v: '49410' },
  { k: t('Incorporation date'), v: '2011-08-04' },
  { k: t('Registered address'), v: 'Unit 7 Quayside Park, Southampton SO14 3QT', variant: 'addr' },
];

const TURNOVER_BARS = [14, 17, 16, 21, 19, 26, 31, 29, 36, 42, 39, 48, 55, 52, 61, 47, 68, 79, 88, 100];

const getFinancialsRows = (t: Translate): Row[] => [
  { k: t('Turnover 2024'), v: '61,842,000' },
  { k: t('Gross profit'), v: '14,207,000' },
  { k: t('Net assets'), v: '9,336,000' },
  { k: t('Employees'), v: '412' },
];

const getStructureTree = (t: Translate): TreeNode[] => [
  { label: 'Meridian Holdings SA', meta: t('ultimate parent'), level: 0 },
  { label: 'Meridian Logistics Ltd', meta: '86.0%', level: 1 },
  { label: 'Meridian Freight Services Ltd', meta: '100.0%', level: 2 },
  { label: 'Meridian Warehousing NV', meta: '74.5%', level: 2 },
  { label: 'Meridian Logistics Polska Sp. z o.o.', meta: '100.0%', level: 1 },
];

const getStructureRows = (t: Translate): Row[] => [
  { k: t('Registered shareholders'), v: '4' },
  { k: t('Persons with significant control'), v: '2' },
];

const getShareholdersRows = (t: Translate): Row[] => [
  { k: 'Meridian Holdings SA', v: '86.0%' },
  { k: 'A. Whitcombe', v: '9.0%' },
  { k: 'Meridian Employee Trust', v: '5.0%' },
  { k: t('Share class'), v: 'Ordinary GBP 1', variant: 'txt' },
  { k: t('Issued shares'), v: '1,000,000' },
  { k: t('Persons with significant control'), v: '2' },
  { k: t('Last confirmation statement'), v: '2026-04-18' },
];

const getPeople = (t: Translate): Person[] => [
  {
    id: 'em1',
    name: 'A. Whitcombe',
    role: t('Chief Executive Officer'),
    emailMasked: 'a.whit•••@meridian-logistics.example',
    emailFull: 'a.whitcombe@meridian-logistics.example',
    phoneMasked: '+44 20 •••• ••18',
    phoneFull: '+44 20 7946 0118',
  },
  {
    id: 'em2',
    name: 'R. Osei',
    role: t('Chief Financial Officer'),
    emailMasked: 'r.osei•••@meridian-logistics.example',
    emailFull: 'r.osei@meridian-logistics.example',
    phoneMasked: '+44 20 •••• ••24',
    phoneFull: '+44 20 7946 0124',
  },
  {
    id: 'em3',
    name: 'L. Ferrante',
    role: t('Head of Procurement'),
    emailMasked: 'l.ferr•••@meridian-logistics.example',
    emailFull: 'l.ferrante@meridian-logistics.example',
    phoneMasked: '+44 20 •••• ••41',
    phoneFull: '+44 20 7946 0141',
  },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M8 1.5v8.5M8 10 4.8 6.8M8 10l3.2-3.2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M2.4 12v2.1h11.2V12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export const ExtensionHeroSection = () => {
  const { secondaryNavigation } = useContext(ContentContext);
  const { t } = useInt();

  const [activeTab, setActiveTab] = useState<TabId>('status');
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  const stageRef = useRef<HTMLDivElement>(null);

  const tabs = getTabs(t);
  const statusRows = getStatusRows(t);
  const financialsRows = getFinancialsRows(t);
  const structureTree = getStructureTree(t);
  const structureRows = getStructureRows(t);
  const shareholdersRows = getShareholdersRows(t);
  const people = getPeople(t);

  const handleReveal = useCallback((id: string) => {
    setRevealed((prev) => ({ ...prev, [id]: true }));
  }, []);

  const handlePointerMove = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const el = stageRef.current;

    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width;
    const py = (event.clientY - rect.top) / rect.height;

    el.style.setProperty('--rx', `${(py - 0.5) * -6}deg`);
    el.style.setProperty('--ry', `${(px - 0.5) * 8}deg`);
    el.style.setProperty('--mx', `${px * 100}%`);
    el.style.setProperty('--my', `${py * 100}%`);
  }, []);

  const handlePointerLeave = useCallback(() => {
    const el = stageRef.current;

    if (!el) {
      return;
    }

    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
  }, []);

  return (
    <Section className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.copy}>
          <TitleBlock
            size="page"
            align="left"
            title={t('Verify and research any company from your browser.')}
            description={t(
              'A free extension for Chrome and Edge. Open the panel on any company website or LinkedIn page and read the government registry record, then push it to your CRM without leaving the tab.',
            )}
          />

          <div className={styles.actions}>
            <Button type="blue" size="xxl" href="https://www.globaldatabase.com/outreach" suffix={<ArrowIcon />}>
              {t('Install for free')}
            </Button>

            {secondaryNavigation?.book_demo?.slug && (
              <Button type="dark-outline" size="xxl" route={secondaryNavigation.book_demo.slug}>
                {t('Book a demo')}
              </Button>
            )}
          </div>
        </div>

        <div ref={stageRef} className={styles.stage} onMouseMove={handlePointerMove} onMouseLeave={handlePointerLeave}>
          <div className={styles.glow} aria-hidden="true" />

          <div className={styles.panel}>
            <div className={styles.chrome}>
              <span className={cn(styles.chromeDot, styles.chromeDotRed)} />
              <span className={cn(styles.chromeDot, styles.chromeDotAmber)} />
              <span className={cn(styles.chromeDot, styles.chromeDotGreen)} />
              <span className={styles.chromeUrl}>meridian-logistics.example</span>
            </div>

            <div className={styles.glare} aria-hidden="true" />

            <div className={styles.panelHead}>
              <div className={styles.panelName}>Meridian Logistics Ltd</div>

              <span className={styles.liveBadge}>
                <i className={styles.liveDot} />
                {t('Active — no insolvency filings')}
              </span>
            </div>

            <div className={styles.tabs} role="tablist" aria-label={t('Company record sections')}>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  className={cn(styles.tab, activeTab === tab.id && styles.tabActive)}
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className={styles.body}>
              <div key={activeTab} className={styles.pane}>
                {activeTab === 'status' && (
                  <>
                    {statusRows.map((row) => (
                      <div key={row.k} className={styles.row}>
                        <span className={styles.k}>{row.k}</span>
                        <span
                          className={cn(
                            styles.v,
                            row.variant === 'txt' && styles.vTxt,
                            row.variant === 'addr' && styles.vAddr,
                            row.variant === 'ok' && styles.vOk,
                          )}
                        >
                          {row.v}
                        </span>
                      </div>
                    ))}
                  </>
                )}

                {activeTab === 'financials' && (
                  <>
                    <div className={cn(styles.row, styles.rowFlush)}>
                      <span className={styles.k}>{t('Turnover, GBP m')}</span>
                      <span className={cn(styles.v, styles.vTxt)}>{t('20 years filed')}</span>
                    </div>

                    <div
                      className={styles.bars}
                      role="img"
                      aria-label={t('Filed turnover 2005 to 2024, rising from 4.1 to 61.8 million pounds')}
                    >
                      {TURNOVER_BARS.map((height, i) => (
                        <b
                          key={i}
                          className={cn(i === TURNOVER_BARS.length - 1 && styles.barHi)}
                          style={{ height: `${height}%` }}
                        />
                      ))}
                    </div>

                    <div className={styles.barsAxis}>
                      <span>2005</span>
                      <span>2024</span>
                    </div>

                    {financialsRows.map((row) => (
                      <div key={row.k} className={styles.row}>
                        <span className={styles.k}>{row.k}</span>
                        <span className={styles.v}>{row.v}</span>
                      </div>
                    ))}
                  </>
                )}

                {activeTab === 'structure' && (
                  <>
                    {structureTree.map((node) => (
                      <div
                        key={node.label}
                        className={cn(styles.tnode, node.level === 1 && styles.d1, node.level === 2 && styles.d2)}
                      >
                        <span className={styles.tnodeName}>
                          {node.level > 0 && <span className={styles.rail}>└</span>}
                          {node.level === 0 ? <b>{node.label}</b> : node.label}
                        </span>
                        <span className={styles.tnodeMeta}>{node.meta}</span>
                      </div>
                    ))}

                    {structureRows.map((row) => (
                      <div key={row.k} className={cn(styles.row, styles.rowTop)}>
                        <span className={styles.k}>{row.k}</span>
                        <span className={styles.v}>{row.v}</span>
                      </div>
                    ))}
                  </>
                )}

                {activeTab === 'shareholders' && (
                  <>
                    {shareholdersRows.map((row) => (
                      <div key={row.k} className={styles.row}>
                        <span className={styles.k}>{row.k}</span>
                        <span className={cn(styles.v, row.variant === 'txt' && styles.vTxt)}>{row.v}</span>
                      </div>
                    ))}
                  </>
                )}

                {activeTab === 'people' && (
                  <>
                    {people.map((person) => (
                      <div key={person.id} className={styles.person}>
                        <div className={styles.personInfo}>
                          <div className={styles.personName}>{person.name}</div>
                          <div className={styles.personRole}>{person.role}</div>
                          <div className={styles.personContact}>
                            {revealed[`${person.id}-email`] ? person.emailFull : person.emailMasked}
                          </div>
                          <div className={styles.personContact}>
                            {revealed[`${person.id}-phone`] ? person.phoneFull : person.phoneMasked}
                          </div>
                        </div>

                        <div className={styles.personActs}>
                          <button
                            type="button"
                            className={styles.revealBtn}
                            disabled={revealed[`${person.id}-email`]}
                            onClick={() => handleReveal(`${person.id}-email`)}
                          >
                            {revealed[`${person.id}-email`] ? t('Revealed') : t('Reveal email')}
                          </button>

                          <button
                            type="button"
                            className={styles.revealBtn}
                            disabled={revealed[`${person.id}-phone`]}
                            onClick={() => handleReveal(`${person.id}-phone`)}
                          >
                            {revealed[`${person.id}-phone`] ? t('Revealed') : t('Reveal phone')}
                          </button>
                        </div>
                      </div>
                    ))}

                    <div className={styles.row}>
                      <span className={styles.k}>{t('Executives on file')}</span>
                      <span className={styles.v}>27</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className={styles.panelFoot}>
              <button type="button" className={styles.saveBtn}>
                {t('Save to list')}
              </button>

              <button type="button" className={styles.pushBtn}>
                {t('Push to CRM')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
